import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en, { Locale } from './locales/en';
import ru from './locales/ru';
import zh from './locales/zh';
import ar from './locales/ar';

export type Lang = 'en' | 'ru' | 'zh' | 'ar';

export const LANGUAGES: { code: Lang; label: string; native: string; flag: string }[] = [
  { code: 'en', label: 'English', native: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Russian', native: 'Русский', flag: '🇷🇺' },
  { code: 'zh', label: 'Chinese', native: '中文', flag: '🇨🇳' },
  { code: 'ar', label: 'Arabic', native: 'العربية', flag: '🇸🇦' },
];

const DICTS: Record<Lang, Locale> = { en, ru, zh, ar };

const STORAGE_KEY = 'swapme.lang';
const SUPPORTED = new Set<Lang>(['en', 'ru', 'zh', 'ar']);

/** BCP-47 locale tag for date formatting per language. */
const DATE_LOCALE: Record<Lang, string> = {
  en: 'en-GB',
  ru: 'ru-RU',
  zh: 'zh-CN',
  ar: 'ar',
};

function detectInitial(): Lang {
  if (typeof window === 'undefined') return 'en';
  // 1. ?lang=xx query param (enables shareable localized links + SEO)
  const params = new URLSearchParams(window.location.search);
  const q = params.get('lang')?.slice(0, 2).toLowerCase();
  if (q && SUPPORTED.has(q as Lang)) {
    try { localStorage.setItem(STORAGE_KEY, q); } catch { /* ignore */ }
    // Clean the URL so it doesn't stick around
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('lang');
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
    return q as Lang;
  }
  // 2. Saved preference
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED.has(saved as Lang)) return saved as Lang;
  // 3. Browser preference
  const nav = navigator.language?.slice(0, 2).toLowerCase();
  if (nav && SUPPORTED.has(nav as Lang)) return nav as Lang;
  return 'en';
}

interface I18nValue {
  lang: Lang;
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLang: (l: Lang) => void;
  /** Interpolate {placeholders} into a string. */
  fmt: (template: string, vars?: Record<string, string | number>) => string;
  /** Locale-aware date string. */
  formatDate: (d: Date) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectInitial);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  }, []);

  const locale = DICTS[lang] ?? en;
  const dir: 'ltr' | 'rtl' = locale.meta.dir === 'rtl' ? 'rtl' : 'ltr';

  // Sync <html lang> and <html dir> so the browser, screen readers and CSS
  // logical properties all follow the active language.
  useEffect(() => {
    const root = document.documentElement;
    root.lang = lang;
    root.dir = dir;
  }, [lang, dir]);

  const fmt = useCallback((template: string, vars?: Record<string, string | number>) => {
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
  }, []);

  const formatDate = useCallback((d: Date) => {
    try {
      return d.toLocaleDateString(DATE_LOCALE[lang], { year: 'numeric', month: 'long' });
    } catch {
      return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'long' });
    }
  }, [lang]);

  const value = useMemo<I18nValue>(
    () => ({ lang, locale, dir, setLang, fmt, formatDate }),
    [lang, locale, dir, setLang, fmt, formatDate],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

/** Convenience: returns just the locale dictionary. */
export function useT(): Locale {
  return useI18n().locale;
}
